import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Badge, Button } from '@/components/ui/primitives';
import { api, ApiError } from '@/lib/api';
import { channelLabel } from '@/data/studio-layouts';
import { mirrorFormatLabel, type MirrorPost } from '@/data/social-mirror';
import type { PublishState, ReviewStatus } from '@/types/social';
import type { AutosaveStatus } from './useStudioAutosave';
import {
  PUBLISH_ENDPOINT, alreadyPublished, buildPublishRequest, canPublish, mapPublishError,
  parsePublishSuccess, publishBlockMessage, publishBlockReason,
  type PublishResult, type PublishSuccess,
} from '@/data/social-publish';

/* ----------------------------------------------------------------------------
 * PublishControl — the D.2 gated "Publish" surface. Sits beside the
 * "What will post" mirror (PublishMirror) and drives the ONE wired publish
 * route (POST /v1/social/publish, SocialPublishRoutes.php).
 *
 * SAFETY MODEL (publish-triggering, §1-adjacent):
 *   - VISIBILITY: rendered ONLY when review_status==='approved' — absent, never
 *     disabled-but-visible, for any other status. FE twin of the fail-closed
 *     service approval gate.
 *   - NO RE-PUBLISH (B2): an approved draft that ALREADY published (publish
 *     state / provider_ref receipt) shows the honest "Already published" state
 *     instead of a button — the BE has no idempotency gate, so re-offering the
 *     button would mint a duplicate live post.
 *   - SAVE TRUTH (B1): the POST publishes the PERSISTED row, so the confirm
 *     must equal the persisted state. Opening the confirm flushes the autosave
 *     (saveNow) and snapshots the editor state; the POST is blocked (pure
 *     publishBlockReason gate, re-checked synchronously at click time) while
 *     saving / save-failed / conflicted / edited-since-snapshot.
 *   - CONFIRM TRUTH (B3): the confirm reuses the SAME mirror the "What will
 *     post" panel shows (media kind + caption + FB first-comment). For
 *     repurpose-sourced FB drafts whose provenance blob the FE cannot fully
 *     see, an honest "may include a first-comment link" note renders instead
 *     of silence.
 *   - ONE click -> ONE confirm -> ONE POST. No optimistic UI (state flips only
 *     on the real receipt), NO retry loop, NO auto-publish path.
 * -------------------------------------------------------------------------- */

/** Autosave signals the save-truth gate consumes (from useStudioAutosave). */
export interface PublishSaveGate {
  status: AutosaveStatus;
  conflict: boolean;
  /** JSON of the CURRENT editable state (same serialization the autosave uses). */
  serialized: string;
  /** flush any pending autosave immediately (no-op when clean/conflicted). */
  saveNow: () => void;
}

type Phase = 'idle' | 'confirm';

export function PublishControl({ draftId, reviewStatus, publishState, providerRef, mirror, saveGate, firstCommentUncertain }: {
  draftId: number;
  reviewStatus: ReviewStatus;
  publishState: PublishState;
  providerRef: string | null;
  mirror: MirrorPost;
  saveGate: PublishSaveGate;
  /** B3: FB repurpose-class draft whose provenance may carry a hidden link. */
  firstCommentUncertain: boolean;
}) {
  const qc = useQueryClient();
  const [phase, setPhase] = useState<Phase>('idle');
  // B1: the editor-state snapshot taken when the confirm opened; any change
  // after this makes the shown confirm stale -> POST blocked.
  const [confirmSnapshot, setConfirmSnapshot] = useState<string | null>(null);

  const publish = useMutation({
    mutationFn: async (): Promise<PublishSuccess> => {
      // One POST. buildPublishRequest omits project_id (self-route by domain)
      // and forwards only a real link placement from the mirror.
      const body = buildPublishRequest(draftId, { linkPlacement: mirror.linkPlacement });
      const res = await api.post<unknown>(PUBLISH_ENDPOINT, body);
      const out = parsePublishSuccess(res);
      if (!out.ok) {
        // N5: a 200 body without the contract's ok:true is NOT success — fail
        // honest rather than render a "Published" receipt for it.
        throw new ApiError(500, 'publish_error', 'Publish response was not recognised.');
      }
      return out;
    },
    onSuccess: () => {
      // The receipt advances publish_state server-side; refetch so the page's
      // status chip reflects the real published state (no optimistic flip).
      void qc.invalidateQueries({ queryKey: ['social-content'] });
    },
    onError: () => {
      // N6: re-sync the draft (e.g. a stale-cache approval revoked server-side
      // -> 409) so a stale "approved" button cannot persist after the refusal.
      void qc.invalidateQueries({ queryKey: ['social-content'] });
    },
  });

  // VISIBILITY RULE — absent entirely unless approved.
  if (reviewStatus !== 'approved') return null;

  const platformLabel = channelLabel(mirror.platform as never);

  // B2 — already published: honest receipt state, no button, no re-publish.
  if (alreadyPublished(publishState, providerRef) && !publish.data) {
    return (
      <div className="rounded-lg border border-border-default bg-surface p-4">
        <Header platformLabel={platformLabel} />
        <div className="mt-3 rounded-md border border-success/30 bg-success/5 p-3">
          <p className="text-sm font-medium text-success">Already published</p>
          <p className="mt-1 text-xs text-text-secondary">
            This draft has already been posted to the connected account. Publishing it again would create a duplicate post, so it is not offered here.
          </p>
          {providerRef ? <div className="mt-2 text-xs"><Row label="Post reference" value={providerRef} /></div> : null}
        </div>
      </div>
    );
  }
  // Belt-and-braces: canPublish is the single gate the truth table locks.
  if (!canPublish(reviewStatus, publishState, providerRef) && !publish.data) return null;

  // A blocked mirror (IG without media, or an unsupported channel) cannot
  // publish honestly — no POST path is offered.
  if (mirror.format === 'blocked') {
    const reason = mirror.blockedReason === 'ig_requires_media'
      ? 'Instagram requires an image or video. Add media before publishing.'
      : mirror.supported
        ? 'This draft will not publish in its current state.'
        : `${mirror.platform} is not a connected publish target, so this draft cannot be published here.`;
    return (
      <div className="rounded-lg border border-border-default bg-surface p-4">
        <Header platformLabel={platformLabel} />
        <p className="mt-2 text-xs text-warning">{reason} See “What will post” above.</p>
      </div>
    );
  }

  // B1 gate — recomputed every render from live autosave signals.
  const blockReason = publishBlockReason({
    status: saveGate.status,
    conflict: saveGate.conflict,
    dirtySinceConfirm: confirmSnapshot !== null && confirmSnapshot !== saveGate.serialized,
  });

  const openConfirm = () => {
    // Flush any pending local changes to the server NOW (no-op when clean or
    // conflicted), then snapshot what the confirm will show. The confirm stays
    // POST-blocked until the flush settles ('saved') and the snapshot matches.
    saveGate.saveNow();
    setConfirmSnapshot(saveGate.serialized);
    setPhase('confirm');
  };

  const onConfirm = () => {
    // Synchronous guards (N2 double-click + B1 save-truth): never trust the
    // rendered disabled state alone.
    if (publish.isPending) return;
    if (publishBlockReason({
      status: saveGate.status,
      conflict: saveGate.conflict,
      dirtySinceConfirm: confirmSnapshot !== null && confirmSnapshot !== saveGate.serialized,
    }) !== null) return;
    publish.mutate();
  };

  const success = publish.data;
  const errInfo = publish.error
    ? publish.error instanceof ApiError
      ? mapPublishError({ status: publish.error.status, code: publish.error.code, message: publish.error.message })
      : { title: 'Publish failed', detail: 'Publishing failed unexpectedly. The post may not have been created — check the platform before retrying.' }
    : null;

  return (
    <div className="rounded-lg border border-border-default bg-surface p-4">
      <Header platformLabel={platformLabel} />

      {/* SUCCESS — the real receipt surface (provider_ref / published_at). */}
      {success ? (
        <PublishedSurface results={success.results} />
      ) : phase === 'confirm' ? (
        <ConfirmPanel
          mirror={mirror}
          platformLabel={platformLabel}
          pending={publish.isPending}
          blockReason={blockReason}
          firstCommentUncertain={firstCommentUncertain}
          error={errInfo}
          onCancel={() => { if (!publish.isPending) { setPhase('idle'); setConfirmSnapshot(null); publish.reset(); } }}
          onConfirm={onConfirm}
        />
      ) : (
        <div className="mt-3">
          <p className="mb-2 text-xs text-text-secondary">
            This draft is approved. Publishing posts it live to {platformLabel} exactly as shown in “What will post”.
          </p>
          {/* N4: the opener does NOT publish — it opens the confirmation. */}
          <Button variant="ai" onClick={openConfirm}>Publish…</Button>
        </div>
      )}
    </div>
  );
}

function Header({ platformLabel }: { platformLabel: string }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold text-text-primary">Publish</span>
        <Badge tone="neutral">{platformLabel}</Badge>
      </div>
      <span className="text-[11px] text-text-tertiary">Posts live to the connected account</span>
    </div>
  );
}

/**
 * The explicit confirm step BEFORE the POST — shows EXACTLY what will post,
 * reusing the mirror data (format + media kind + caption snippet + FB
 * first-comment). No POST fires until "Confirm publish" is pressed here, and
 * the button stays blocked while local state != persisted state (B1).
 */
function ConfirmPanel({ mirror, platformLabel, pending, blockReason, firstCommentUncertain, error, onCancel, onConfirm }: {
  mirror: MirrorPost;
  platformLabel: string;
  pending: boolean;
  blockReason: ReturnType<typeof publishBlockReason>;
  firstCommentUncertain: boolean;
  error: { title: string; detail: string } | null;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const mediaLine = mirror.mediaUrl
    ? mirror.mediaKind === 'video' ? 'A video' : 'An image'
    : 'No media (text only)';
  const snippet = captionSnippet(mirror.caption);
  const blocked = blockReason !== null;
  return (
    <div className="mt-3 space-y-3 rounded-md border border-ai/30 bg-ai/5 p-3">
      <p className="text-xs font-medium text-text-primary">
        Publish this {mirrorFormatLabel(mirror.format).toLowerCase()} to {platformLabel}?
      </p>
      <dl className="space-y-1 text-xs">
        <Row label="Platform" value={platformLabel} />
        <Row label="Format" value={mirrorFormatLabel(mirror.format)} />
        <Row label="Media" value={mediaLine} />
        <div>
          <dt className="text-text-secondary">Caption</dt>
          <dd className="mt-0.5 whitespace-pre-wrap break-words text-text-primary">
            {snippet !== '' ? snippet : <span className="italic text-text-tertiary">No caption text.</span>}
          </dd>
        </div>
        {mirror.firstCommentNote ? (
          <div>
            <dt className="text-text-secondary">First comment</dt>
            <dd className="mt-0.5 whitespace-pre-wrap break-words text-text-primary">{mirror.firstCommentNote}</dd>
          </div>
        ) : firstCommentUncertain && mirror.platform === 'facebook' ? (
          /* B3: repurpose-class draft — the provenance blob may carry a blog
           * link this client cannot see; say so honestly instead of silence. */
          <div>
            <dt className="text-text-secondary">First comment</dt>
            <dd className="mt-0.5 text-text-primary">
              This repurposed draft may include its blog link posted as a first comment.
            </dd>
          </div>
        ) : null}
      </dl>

      {/* B1: save-truth blocking state — the POST is disabled while shown. */}
      {blocked ? (
        <div className="rounded-md border border-warning/30 bg-warning/5 p-2" role="status">
          <p className="text-xs font-medium text-warning">
            {blockReason === 'saving' ? 'Saving changes…' : 'Cannot publish yet'}
          </p>
          <p className="mt-0.5 text-xs text-text-secondary">{publishBlockMessage(blockReason)}</p>
        </div>
      ) : null}

      {error ? (
        <div className="rounded-md border border-danger/30 bg-danger/5 p-2">
          <p className="text-xs font-medium text-danger">{error.title}</p>
          <p className="mt-0.5 text-xs text-text-secondary">{error.detail}</p>
        </div>
      ) : null}

      <div className="flex gap-2">
        {/* N4: distinct label from the opener ("Publish…"). */}
        <Button variant="ai" onClick={onConfirm} disabled={pending || blocked}>
          {pending ? 'Publishing…' : 'Confirm publish'}
        </Button>
        <Button variant="secondary" onClick={onCancel} disabled={pending}>Cancel</Button>
      </div>
      <p className="text-[11px] text-text-tertiary">This posts live and cannot be undone from here.</p>
    </div>
  );
}

/** The real published receipt (provider_ref / published_at, per platform). */
function PublishedSurface({ results }: { results: PublishResult[] }) {
  return (
    <div className="mt-3 space-y-2 rounded-md border border-success/30 bg-success/5 p-3">
      <p className="text-sm font-medium text-success">Published</p>
      {results.map((r, i) => (
        <div key={`${r.platform}-${i}`} className="rounded-md border border-border-subtle bg-surface p-2 text-xs">
          <div className="flex items-center gap-2">
            <Badge tone="neutral">{channelLabel(r.platform as never)}</Badge>
            <span className="text-text-secondary">{mirrorFormatLabel(r.format as never)}</span>
          </div>
          {r.permalink ? (
            <p className="mt-1">
              <a href={r.permalink} target="_blank" rel="noreferrer" className="text-ai underline">View post</a>
            </p>
          ) : null}
          {r.provider_ref ? <Row label="Post reference" value={r.provider_ref} /> : null}
          {r.published_at ? <Row label="Published at" value={r.published_at} /> : null}
          {r.first_comment_status && r.first_comment_status !== 'skipped'
            ? <Row label="First comment" value={r.first_comment_status} />
            : null}
        </div>
      ))}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-text-secondary">{label}</dt>
      <dd className="break-all text-right text-text-primary">{value}</dd>
    </div>
  );
}

/** First ~160 chars of the caption for the confirm preview (mirror caption). */
function captionSnippet(caption: string): string {
  const c = (caption ?? '').trim();
  return c.length > 160 ? c.slice(0, 160).trimEnd() + '…' : c;
}
